// src/components/Modals/StaffModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

const ROLES = ["Owner", "Manager", "Technician", "Maker", "Member"];

function num(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

export default function StaffModal({ initialStaff = null, onSave, onClose }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("Member");
  const [email, setEmail] = useState("");
  const [color, setColor] = useState("#6b7280"); // zinc-500 default
  const [notes, setNotes] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [hourlyRate, setHourlyRate] = useState("");
  const [hoursPerWeek, setHoursPerWeek] = useState("");
  const [salary, setSalary] = useState("");

  // pay mode: "hourly" or "salary"
  const [payMode, setPayMode] = useState("hourly");

  const canSave = name.trim().length > 0;
  const firstInputRef = useRef(null);

  // Prefill when editing
  useEffect(() => {
    if (!initialStaff) return;
    setName(initialStaff.name || "");
    setRole(initialStaff.role || "Member");
    setEmail(initialStaff.email || "");
    setColor(initialStaff.color || "#6b7280");
    setNotes(initialStaff.notes || "");
    setIsActive(initialStaff.isActive !== false);

    // infer pay mode
    const sal = Number(initialStaff.salary);
    const hr = Number(initialStaff.hourlyRate);
    const hw = Number(initialStaff.hoursPerWeek);

    if (Number.isFinite(sal) && sal > 0) {
      setPayMode("salary");
      setSalary(String(sal));
      setHourlyRate("");
      setHoursPerWeek(String(Number.isFinite(hw) ? hw : ""));
    } else {
      setPayMode("hourly");
      setHourlyRate(String(Number.isFinite(hr) ? hr : ""));
      setHoursPerWeek(String(Number.isFinite(hw) ? hw : ""));
      setSalary("");
    }
  }, [initialStaff]);

  // Autofocus + keyboard shortcuts (Esc close, ⌘/Ctrl+S save)
  useEffect(() => {
    firstInputRef.current?.focus?.();
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]); // eslint-disable-line

  const headerTitle = initialStaff ? "Edit Staff" : "Add Staff";

  const previewBadge = useMemo(() => {
    const n = (name || "").trim();
    const initials = n
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() || "")
      .join("");
    return initials || "";
  }, [name]);

  function handleSave() {
    if (!canSave) return;

    const payload = {
      name: name.trim(),
      role: role || "Member",
      email: (email || "").trim(),
      color: color || "#6b7280",
      notes: notes || "",
      isActive: !!isActive,
    };

    if (payMode === "salary") {
      payload.salary = num(salary, 0);
      payload.hourlyRate = 0;
      payload.hoursPerWeek = num(hoursPerWeek, 0);
    } else {
      payload.salary = 0;
      payload.hourlyRate = num(hourlyRate, 0);
      payload.hoursPerWeek = num(hoursPerWeek, 0);
    }

    try {
      onSave?.(payload);
    } catch (e) {
      console.warn("[StaffModal] onSave error:", e);
    }
  }

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="staff-title"
      onMouseDown={onBackdrop}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />

      {/* Panel */}
      <div
        className="relative w-full max-w-2xl rounded-xl border border-white/10 bg-zinc-900 text-white shadow-lg"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div
              className="h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold"
              style={{ background: color }}
              aria-hidden="true"
            >
              {previewBadge}
            </div>
            <div id="staff-title" className="text-lg font-semibold">{headerTitle}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={!canSave}
              className={`px-3 py-1.5 rounded text-sm ${
                canSave
                  ? "bg-emerald-500 text-black hover:opacity-90"
                  : "bg-emerald-900/50 text-emerald-100/60 cursor-not-allowed"
              }`}
              title="⌘/Ctrl+S"
            >
              Save
            </button>
            <button
              onClick={() => onClose?.()}
              className="px-3 py-1.5 rounded text-sm bg-zinc-700 hover:bg-zinc-600"
            >
              Close
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-4">
          {/* Basics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs opacity-70">Name</label>
              <input
                ref={firstInputRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                className="w-full px-3 py-2 rounded bg-zinc-800 border border-white/10 outline-none"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs opacity-70">Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full px-3 py-2 rounded bg-zinc-800 border border-white/10 outline-none"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs opacity-70">Email</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@studio.com"
                className="w-full px-3 py-2 rounded bg-zinc-800 border border-white/10 outline-none"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs opacity-70">Badge Colour</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-10 w-16 p-1 rounded bg-transparent border border-white/10"
                  aria-label="Choose colour"
                />
                <span className="text-xs opacity-70">{color}</span>
              </div>
            </div>
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-3">
            <input
              id="activeToggle"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="activeToggle" className="text-sm">Active member</label>
          </div>

          {/* Compensation */}
          <div className="rounded-lg border border-white/10 bg-white/5 p-3">
            <div className="text-sm font-semibold mb-2">Compensation</div>

            <div className="flex flex-wrap gap-3 mb-3">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="paymode"
                  checked={payMode === "hourly"}
                  onChange={() => setPayMode("hourly")}
                />
                Hourly
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="paymode"
                  checked={payMode === "salary"}
                  onChange={() => setPayMode("salary")}
                />
                Salary (monthly)
              </label>
            </div>

            {payMode === "hourly" ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="text-xs opacity-70">Hourly rate (A$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(e.target.value)}
                    placeholder="e.g. 32.50"
                    className="w-full px-3 py-2 rounded bg-zinc-800 border border-white/10 outline-none"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs opacity-70">Hours / week</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    inputMode="decimal"
                    value={hoursPerWeek}
                    onChange={(e) => setHoursPerWeek(e.target.value)}
                    placeholder="e.g. 38"
                    className="w-full px-3 py-2 rounded bg-zinc-800 border border-white/10 outline-none"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs opacity-70">Monthly (est.)</label>
                  <div className="px-3 py-2 rounded bg-zinc-800 border border-white/10 text-sm">
                    {(() => {
                      const est = num(hourlyRate, 0) * (num(hoursPerWeek, 0) * 4.33);
                      return isFinite(est) ? `A$${est.toFixed(2)}` : "—";
                    })()}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1 md:col-span-1">
                  <label className="text-xs opacity-70">Salary / month (A$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    value={salary}
                    onChange={(e) => setSalary(e.target.value)}
                    placeholder="e.g. 5200"
                    className="w-full px-3 py-2 rounded bg-zinc-800 border border-white/10 outline-none"
                  />
                </div>
                <div className="space-y-1 md:col-span-1">
                  <label className="text-xs opacity-70">Hours / week (optional)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    inputMode="decimal"
                    value={hoursPerWeek}
                    onChange={(e) => setHoursPerWeek(e.target.value)}
                    placeholder="for planning"
                    className="w-full px-3 py-2 rounded bg-zinc-800 border border-white/10 outline-none"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <label className="text-xs opacity-70">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Optional notes about this team member…"
              className="w-full px-3 py-2 rounded bg-zinc-800 border border-white/10 outline-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
